import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { Storage, getDownloadURL, ref, uploadBytes } from '@angular/fire/storage';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ArtistService } from '../../services/artist.service';
import { ArtistStatement } from '../../services/artist-statement.service';
import { ArtistMonthlyStatementService } from '../../services/artist-monthly-statement.service';
import { FrameOwnershipService } from '../../services/frame-ownership.service';
import { PdfGeneratorService } from '../../services/pdf-generator.service';
import { EmailFormComponent } from '../../email-form/email-form.component';
import { ArtistStatementTemplateComponent } from '../artist-statement-template/artist-statement-template.component';
import {
  ArtistMonthStatement,
  MonthlyStatements,
  STATEMENT_COMPANY_NAME,
  StatementArtist,
  buildMonthlyStatements,
  previousFullMonth,
  statementPeriods,
  statementSignature
} from '../../utils/artist-monthly-statement.util';

/**
 * KAN-147: the "Monthly statements" tab of /home/artist-statement. Generated on demand per month from frame usage
 * (all devices, soft-deleted included) and frame ownership (KAN-149). Grid actions View and Email, like the SMRS invoice
 * grid (2026-10-27 user change: no Issue button, no DRAFT mark). Emailing saves a revisioned snapshot in
 * artist_statements first, so what was sent is kept; an unchanged statement reuses its last revision.
 */
@Component({
  selector: 'app-artist-monthly-statements',
  standalone: true,
  imports: [CommonModule, FormsModule, MatSnackBarModule, ArtistStatementTemplateComponent, EmailFormComponent],
  templateUrl: './artist-monthly-statements.component.html',
  styleUrls: ['./artist-monthly-statements.component.scss']
})
export class ArtistMonthlyStatementsComponent implements OnInit, OnDestroy {
  private monthly = inject(ArtistMonthlyStatementService);
  private frameOwnership = inject(FrameOwnershipService);
  private artistService = inject(ArtistService);
  private pdfService = inject(PdfGeneratorService);
  private snackBar = inject(MatSnackBar);
  private auth = inject(Auth);
  private storage = inject(Storage);
  private cd = inject(ChangeDetectorRef);

  readonly periods = statementPeriods(new Date());
  period = previousFullMonth(new Date());

  isLoading = false;
  progress = { done: 0, total: 0 };
  loadError: string | null = null;
  result: MonthlyStatements | null = null;
  saved = new Map<string, ArtistStatement>();
  thumbUrls = new Map<string, string | null>();
  private thumbsVersion = 0;
  private thumbPending = new Map<string, Promise<void>>();
  private docCache = new Map<string, { stmt: ArtistMonthStatement; saved: ArtistStatement | undefined; version: number; doc: ArtistStatement }>();
  private runToken = 0;

  /** Statement shown in the popup. */
  selected: ArtistMonthStatement | null = null;
  /** Statement rendered off-screen for PDF output (the popup's statement, or a grid row being emailed). */
  printTarget: ArtistMonthStatement | null = null;
  busyArtistId: string | null = null;
  isGeneratingPdf = false;
  /** The KPI + ranking summary PDF is being generated (renders its hidden host). */
  isExportingSummary = false;
  readonly companyName = STATEMENT_COMPANY_NAME;
  readonly today = new Date();

  isEmailOpen = false;
  emailData: { to: string; subject: string; body: string; attachments: { name: string; url: string; storagePath?: string }[] } | null = null;
  private emailDoc: ArtistStatement | null = null;
  private emailWatchers: (() => void)[] = [];

  ngOnInit() {
    this.load();
  }

  ngOnDestroy() {
    this.emailWatchers.forEach(stop => stop());
  }

  async load() {
    const token = ++this.runToken;
    this.isLoading = true;
    this.loadError = null;
    this.result = null;
    this.progress = { done: 0, total: 0 };
    try {
      const [usage, ownerships, artists, saved] = await Promise.all([
        this.monthly.loadMonthUsage(this.period, (done, total) => { if (token === this.runToken) this.progress = { done, total }; }),
        this.frameOwnership.getAllOwnerships(),
        firstValueFrom(this.artistService.getArtists()),
        this.monthly.getIssued(this.period)
      ]);
      if (token !== this.runToken) return; // a newer period was selected: never show stale numbers
      const artistsById = new Map<string, StatementArtist>(artists.map((a: any) => [a.id, a]));
      this.result = buildMonthlyStatements(usage, ownerships, artistsById);
      const settled = await Promise.all([...saved.values()].map(s => this.monthly.settlePendingEmail(s)));
      if (token !== this.runToken) return;
      this.saved = new Map(settled.map(s => [s.artistId, s]));
      this.loadThumbs();
    } catch (e: any) {
      console.error('Error generating monthly statements:', e);
      if (token === this.runToken) this.loadError = 'The statements could not be generated. Check the connection and press Refresh.';
    } finally {
      if (token === this.runToken) this.isLoading = false;
    }
  }

  private loadThumbs() {
    // only frames shown on statements: company-owned and owner-deleted frames are not displayed with images
    const frames = this.result?.artists.flatMap(a => a.lines) || [];
    for (const f of frames) {
      if (this.thumbUrls.has(f.frameId)) continue;
      this.thumbUrls.set(f.frameId, null);
      // same image resolution as the Frame Report, with the device/theme/group recorded on the counter
      const pending = this.frameOwnership.usageImageUrl(f.frameId, f.fpImagePath, f.source).then(url => {
        this.thumbUrls.set(f.frameId, url);
        this.thumbPending.delete(f.frameId);
        this.thumbsVersion++;
      });
      this.thumbPending.set(f.frameId, pending);
    }
  }

  /** The statement's frame images are still being looked up (popup spinner; PDF and Email wait for them). */
  isStatementLoading(stmt: ArtistMonthStatement | null): boolean {
    return !!stmt && stmt.lines.some(l => this.thumbPending.has(l.frameId));
  }

  private async waitForThumbs(stmt: ArtistMonthStatement): Promise<void> {
    await Promise.all(stmt.lines.map(l => this.thumbPending.get(l.frameId)).filter(Boolean));
    this.cd.detectChanges();
  }

  /**
   * Waits until every <img> in the PDF host has finished downloading. The URLs being known is not enough: html2canvas
   * captures a still-loading image as blank, which is how emailed statements went out without frame images.
   */
  private async waitForImagesLoaded(content: HTMLElement, timeoutMs = 20000): Promise<void> {
    const imgs = Array.from(content.querySelectorAll('img'));
    const loads = imgs.map(img => img.complete ? Promise.resolve() : new Promise<void>(resolve => {
      img.addEventListener('load', () => resolve(), { once: true });
      img.addEventListener('error', () => resolve(), { once: true });
    }));
    let timer: any;
    const timedOut = new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(true), timeoutMs); });
    const late = await Promise.race([Promise.all(loads).then(() => false), timedOut]);
    clearTimeout(timer);
    const broken = imgs.filter(img => !img.complete || img.naturalWidth === 0).length;
    if (late || broken > 0) throw new Error(`${broken || 'Some'} frame image(s) could not be loaded. Check the connection and try again.`);
  }

  // --- Status ---

  /** The saved snapshot still matches the current numbers. */
  private isCurrent(stmt: ArtistMonthStatement): boolean {
    const s = this.saved.get(stmt.artistId);
    return !!s && s.signature === statementSignature(stmt);
  }

  badgeOf(stmt: ArtistMonthStatement): { text: string; cls: string } {
    const s = this.saved.get(stmt.artistId);
    if (!s || s.emailStatus === 'not_sent' || !s.emailStatus) {
      return s && !this.isCurrent(stmt) ? { text: 'Changed', cls: 'status-warn' } : { text: 'Not sent', cls: 'status-dim' };
    }
    if (!this.isCurrent(stmt)) return { text: 'Changed since sent', cls: 'status-warn' };
    if (s.emailStatus === 'sent') return { text: `Emailed r${s.revision}`, cls: 'status-paid' };
    if (s.emailStatus === 'pending') return { text: 'Sending…', cls: 'status-info' };
    return { text: 'Email failed', cls: 'status-warn' };
  }

  get missingDataNames(): string[] {
    return (this.result?.missingData || []).map(d => d.deviceName);
  }

  emailDisabledReason(stmt: ArtistMonthStatement): string | null {
    if (stmt.rateMissing) return 'Rate missing: set a commission for this artist in Artist Management first.';
    if (!stmt.email) return 'Add an email in Artist Management.';
    return null;
  }

  // --- Statement popup ---

  openStatement(stmt: ArtistMonthStatement) {
    this.selected = stmt;
    this.printTarget = stmt;
  }

  closeStatement() {
    if (this.isGeneratingPdf || this.busyArtistId) return;
    this.selected = null;
  }

  /**
   * The document shown and printed: the saved snapshot when it still matches, otherwise the current numbers. Cached until a
   * thumbnail arrives or the statement/snapshot changes, so the template gets a stable object between change detections.
   */
  documentFor(stmt: ArtistMonthStatement | null): ArtistStatement | null {
    if (!stmt) return null;
    const saved = this.saved.get(stmt.artistId);
    const hit = this.docCache.get(stmt.artistId);
    if (hit && hit.stmt === stmt && hit.saved === saved && hit.version === this.thumbsVersion) return hit.doc;
    const doc = this.buildDocument(stmt);
    this.docCache.set(stmt.artistId, { stmt, saved, version: this.thumbsVersion, doc });
    return doc;
  }

  private buildDocument(stmt: ArtistMonthStatement): ArtistStatement {
    if (this.isCurrent(stmt)) {
      // snapshots saved before an image was found keep an empty frameImage: show the looked-up thumbnail instead
      const saved = this.saved.get(stmt.artistId)!;
      return { ...saved, items: saved.items.map(i => ({ ...i, frameImage: i.frameImage || this.thumbUrls.get(i.frameId) || '' })) };
    }
    return {
      period: this.period,
      companyId: '',
      companyName: STATEMENT_COMPANY_NAME,
      artistId: stmt.artistId,
      artistName: stmt.artistName,
      items: stmt.lines.map(l => ({ frameId: l.frameId, frameImage: this.thumbUrls.get(l.frameId) || '', quantity: l.prints, commission: l.rate ?? 0, total: l.amount ?? 0 })),
      totalQuantity: stmt.totalPrints,
      totalAmount: stmt.totalAmount ?? 0,
      source: 'automated'
    };
  }

  // --- PDF (same flow as the SMRS invoice: invoice.component handlePdfAction) ---

  private async handlePdfAction(stmt: ArtistMonthStatement, action: 'save' | 'open' | 'blob'): Promise<Blob | void> {
    let pdfWindow: Window | null = null;
    if (action === 'open') {
      // open the window first so the popup blocker allows it
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) pdfWindow.document.write('<html><body><p>Generating PDF... Please wait.</p></body></html>');
    }
    this.printTarget = stmt;
    this.isGeneratingPdf = true;
    try {
      await this.waitForThumbs(stmt); // the PDF never goes out without its frame images
      this.cd.detectChanges();
      const content = document.getElementById('artist-monthly-pdf-content');
      if (!content) {
        if (pdfWindow) pdfWindow.close();
        throw new Error('The statement could not be prepared.');
      }
      await this.waitForImagesLoaded(content);
      await new Promise(resolve => setTimeout(resolve, 300)); // let the decoded images paint before capture
      const filename = `Artist_Statement_${this.period.replace('/', '-')}_${stmt.artistName.replace(/\s+/g, '_')}.pdf`;
      if (action === 'save') await this.pdfService.savePdf(content, filename, 'portrait');
      else if (action === 'open') await this.pdfService.openPdf(content, filename, 'portrait', pdfWindow);
      else return await this.pdfService.generateBlob(content, filename);
    } catch (e: any) {
      if (pdfWindow) pdfWindow.close();
      this.snackBar.open(`The PDF could not be generated: ${e?.message || 'unknown error'}`, 'Dismiss');
    } finally {
      this.isGeneratingPdf = false;
    }
  }

  /** Downloads the month's KPIs and ranking (no Status or Actions columns), styled like the Profit Report's Total P/L PDF. */
  async exportSummaryPdf() {
    if (!this.result || this.isExportingSummary) return;
    this.isExportingSummary = true;
    try {
      this.cd.detectChanges();
      await new Promise(resolve => setTimeout(resolve, 300));
      const content = document.getElementById('artist-summary-pdf-content');
      if (!content) throw new Error('The summary could not be prepared.');
      await this.pdfService.savePdf(content, `Artist_Statement_Summary_${this.period.replace('/', '-')}.pdf`, 'portrait');
    } catch (e: any) {
      this.snackBar.open(`The PDF could not be generated: ${e?.message || 'unknown error'}`, 'Dismiss');
    } finally {
      this.isExportingSummary = false;
    }
  }

  async printPdf() {
    if (this.selected) await this.handlePdfAction(this.selected, 'open');
  }

  async savePdf() {
    if (this.selected) await this.handlePdfAction(this.selected, 'save');
  }

  // --- Email (accounting composer, as the SMRS invoice email) ---

  /** Saves a new revision when the numbers changed (or none exists yet); reuses the last one otherwise. */
  private async snapshotFor(stmt: ArtistMonthStatement): Promise<ArtistStatement> {
    if (this.isCurrent(stmt)) return this.saved.get(stmt.artistId)!;
    const snap = await this.monthly.issue(this.period, stmt, this.saved.get(stmt.artistId), this.missingDataNames, this.thumbUrls);
    this.saved.set(snap.artistId, snap);
    return snap;
  }

  async emailStatement(stmt: ArtistMonthStatement) {
    const blocked = this.emailDisabledReason(stmt);
    if (blocked) {
      this.snackBar.open(blocked, 'Dismiss', { duration: 6000 });
      return;
    }
    if (this.missingDataNames.length > 0
      && !confirm(`Usage data is incomplete for ${this.period} (${this.missingDataNames.join(', ')}). Email the statement anyway?`)) return;
    this.busyArtistId = stmt.artistId;
    try {
      await this.waitForThumbs(stmt); // the saved snapshot keeps the image URLs, not empty ones
      const doc = await this.snapshotFor(stmt);
      const blob = await this.handlePdfAction(stmt, 'blob');
      if (!blob) return;
      const user = this.auth.currentUser;
      if (!user) throw new Error('Not signed in.');
      const name = `Artist_Statement_${doc.statementNumber}.pdf`;
      const storagePath = `users/${user.uid}/temp_invoices/${Date.now()}_${name}`;
      await uploadBytes(ref(this.storage, storagePath), blob, { contentType: 'application/pdf' });
      const url = await getDownloadURL(ref(this.storage, storagePath));
      this.emailDoc = doc;
      this.emailData = {
        to: stmt.email,
        subject: `Artist Statement ${doc.statementNumber} - ${doc.period}`,
        // HTML paragraphs: the composer is a rich-text editor, plain newlines would collapse into one line
        body: `<p>Dear ${doc.artistName},</p><p>Please find attached your artist statement for ${doc.period}.</p>`
          + `<p>Should you have any questions, please reply to this email.</p><p>Best regards,</p>`
          + `<p>Accounting Department<br>${STATEMENT_COMPANY_NAME}</p>`,
        attachments: [{ name, url, storagePath }]
      };
      this.isEmailOpen = true;
    } catch (e: any) {
      console.error('Error preparing the statement email:', e);
      this.snackBar.open(`The email could not be prepared: ${e?.message || 'unknown error'}`, 'Dismiss');
    } finally {
      this.busyArtistId = null;
    }
  }

  async onEmailSent(event: { to?: string; receiptId?: string }) {
    this.isEmailOpen = false;
    const doc = this.emailDoc;
    if (!doc?.id || !event?.receiptId) return;
    const current = this.saved.get(doc.artistId);
    if (current?.id === doc.id) this.saved.set(doc.artistId, { ...current, emailStatus: 'pending' });
    this.snackBar.open('Email queued. The statement shows Emailed once delivery succeeds.', 'Dismiss', { duration: 6000 });
    const stop = await this.monthly.trackEmail(doc.id, event.receiptId, event.to || '', (status, error) => {
      const latest = this.saved.get(doc.artistId);
      if (latest && latest.id === doc.id) this.saved.set(doc.artistId, { ...latest, emailStatus: status, emailError: error });
      if (status === 'sent') this.snackBar.open(`Statement ${doc.statementNumber} emailed.`, 'Dismiss', { duration: 6000 });
      else this.snackBar.open(`Email failed: ${error}. You can send it again.`, 'Dismiss');
      this.cd.detectChanges();
    });
    this.emailWatchers.push(stop);
  }

  closeEmail() {
    this.isEmailOpen = false;
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.isEmailOpen) return; // the composer closes itself
    if (this.selected) this.closeStatement();
  }
}
